"use client";

import { THome_Config } from "@/app/config";
import { ArrowRight, User, Target, Briefcase } from "lucide-react";
import Link from "next/link";
import React from "react";
import { motion } from "motion/react";

const steps = [
  {
    icon: User,
  },
  {
    icon: Target,
  },
  {
    icon: Briefcase,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.2
    }
  }
};

export default function ZumaForCandidatesSection({config}:{config:THome_Config['candidate']}) {
  return (
    <motion.section 
      className="py-16 lg:py-20 bg-white flex flex-col w-full items-center"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="max-w-6xl w-full px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          className="flex flex-col w-full items-center gap-6 mb-16"
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.7,
                ease: "easeOut"
              }
            }
          }}
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.05 }}
          >
            <div className="inline-flex items-center px-3 py-1 bg-green-50 rounded-lg border border-green-200">
              <span className="text-green-700 text-sm font-medium">{config.tag}</span>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1 
            className="text-gray-900 text-4xl lg:text-5xl text-center font-normal leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {config.title}
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            className="text-gray-600 text-lg text-center max-w-2xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Steps Timeline */}
        <motion.div 
          className="relative flex flex-col gap-12 lg:gap-16 mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.3,
                delayChildren: 0.3
              }
            }
          }}
        >
          {/* Desktop Timeline Line */}
          <motion.div 
            className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-0.5 bg-gradient-to-b from-green-500 via-blue-500 to-purple-500 opacity-30 origin-top" 
            style={{ height: 'calc(100% - 2rem)' }}
            variants={{
              hidden: { scaleY: 0, opacity: 0 },
              visible: {
                scaleY: 1,
                opacity: 1,
                transition: {
                  duration: 1.5,
                  ease: "easeInOut",
                  delay: 0.5
                }
              }
            }}
          />

          {config.steps.map((step, index) => {
            const Icon = steps[index].icon;
            const isRight = step.position === "right";
            
            return(
            <motion.div
              key={index}
              className="flex flex-col lg:flex-row items-center gap-8 w-full relative"
              variants={{
                hidden: { opacity: 0, scale: 0.8 },
                visible: {
                  opacity: 1,
                  scale: 1,
                  transition: {
                    duration: 0.8,
                    ease: "easeOut"
                  }
                }
              }}
            >
              {/* Step Info */}
              <motion.div 
                className={`flex flex-col items-center gap-3 order-1 lg:flex-1 ${
                  isRight
                    ? "lg:order-3 lg:pl-12 lg:items-start"
                    : "lg:order-1 lg:pr-12 lg:items-end"
                }`}
                initial={{ opacity: 0, x: isRight ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <motion.p 
                  className={`text-green-600 text-base font-medium ${
                    isRight ? "lg:text-left" : "lg:text-right"
                  }`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 + 0.2 }}
                >
                  Step {step.number}
                </motion.p>
                <motion.h3 
                  className={`text-gray-900 text-xl lg:text-2xl font-normal text-center leading-tight ${
                    isRight ? "lg:text-left" : "lg:text-right"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 + 0.3 }}
                >
                  {step.title}
                </motion.h3>
              </motion.div>

              {/* Timeline Dot */}
              <motion.div 
                className="flex items-center justify-center order-2 lg:order-2 relative z-10"
                variants={ {
                  hidden: { scale: 0, opacity: 0 },
                  visible: {
                    scale: 1,
                    opacity: 1,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut",
                      type: "spring",
                      stiffness: 200
                    }
                  }
                }}
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.3 + 0.5 }}
              >
                <motion.div 
                  className={`w-4 h-4 lg:w-5 lg:h-5 ${step.dotColor} rounded-full border-4 border-white shadow-lg`}
                  variants={ {
                    animate: {
                      scale: [1, 1.1, 1],
                      transition: {
                        duration: 2,
                        ease: "easeInOut",
                        repeat: Infinity,
                        repeatDelay: 1
                      }
                    }
                  }}
                  animate="animate"
                  whileHover={{ scale: 1.2 }}
                />
                {/* Mobile Vertical Line */}
                {index < steps.length - 1 && (
                  <motion.div 
                    className="absolute top-6 left-1/2 transform -translate-x-1/2 w-0.5 h-12 bg-gradient-to-b from-green-500 to-blue-500 opacity-30 lg:hidden origin-top"
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.3 + 0.7 }}
                  />
                )}
              </motion.div>

              {/* Card */}
              <motion.div 
                className={`flex flex-col items-center order-3 lg:flex-1 ${
                  isRight
                    ? "lg:order-1 lg:pr-12"
                    : "lg:order-3 lg:pl-12"
                }`}
                variants={isRight ?  {
                  hidden: { opacity: 0, x: 50 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: {
                      duration: 0.7,
                      ease: "easeOut"
                    }
                  },
                  hover: {
                    y: -5,
                    scale: 1.02,
                    transition: {
                      duration: 0.3,
                      ease: "easeInOut"
                    }
                  }
                } : {
                  hidden: { opacity: 0, x: -50 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: {
                      duration: 0.7,
                      ease: "easeOut"
                    }
                  },
                  hover: {
                    y: -5,
                    scale: 1.02,
                    transition: {
                      duration: 0.3,
                      ease: "easeInOut"
                    }
                  }
                }}
                whileHover="hover"
                transition={{ delay: index * 0.2 + 0.4 }}
              >
                <div className="w-full max-w-md lg:max-w-none bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="flex flex-col gap-4 p-6 lg:p-7 w-full">
                    {/* Card Header */}
                    <motion.div 
                      className={`flex items-center gap-4 w-full ${
                        isRight ? "lg:justify-end lg:flex-row-reverse" : ""
                      }`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y:0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.2 + 0.6 }}
                    >
                      <motion.div 
                        className={`flex w-10 h-10 lg:w-12 lg:h-12 items-center justify-center ${step.bgColor} rounded-lg flex-shrink-0`}
                        variants={ {
                          animate: {
                            y: [-3, 3, -3],
                            transition: {
                              duration: 4,
                              ease: "easeInOut",
                              repeat: Infinity
                            }
                          }
                        }}
                        animate="animate"
                        whileHover={{ 
                          scale: 1.1,
                          rotate: 10,
                          transition: { duration: 0.2 }
                        }}
                      >
                        <Icon className={`w-5 h-5 lg:w-6 lg:h-6 ${step.iconColor}`} />
                      </motion.div>
                      <div className={`flex flex-col ${
                        isRight ? "lg:items-end" : "lg:items-start"
                      } flex-1 min-w-0`}>
                        <h3 className={`text-gray-900 text-lg lg:text-xl font-medium leading-tight ${
                          isRight ? "lg:text-right" : "lg:text-left"
                        }`}>
                          {step.cardTitle}
                        </h3>
                      </div>
                    </motion.div>
                    
                    {/* Card Description */}
                    <motion.div 
                      className={`flex flex-col w-full ${
                        isRight ? "lg:items-end" : "lg:items-start"
                      }`}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.2 + 0.8 }}
                    >
                      <p className={`text-gray-600 text-base leading-relaxed ${
                        isRight ? "lg:text-right" : "lg:text-left"
                      }`}>
                        {step.description}
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
          )}
        </motion.div>

        {/* CTA Button */}
        <motion.div 
          className="flex flex-col items-center justify-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Link
            href={config.cta.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <motion.div
              className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <span>{config.cta.label}</span>
              <motion.div
                animate={{ x: [0, 3, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <ArrowRight className="w-5 h-5" />
              </motion.div>
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </motion.section>
  );
}